/**
 * Keyword bank for smart PDF field detection.
 * When these keywords appear in a PDF, the system can auto-suggest or auto-place the corresponding field type.
 * @see Field Detection Logic Flow - Keyword + Context + Confidence Based Engine
 */
export type FieldTypeKey =
  | 'signature'
  | 'name'
  | 'date'
  | 'email'
  | 'phone'
  | 'company'
  | 'address'
  | 'title'
  | 'id'
  | 'checkbox';

export interface IKeywordCategory {
  fieldType: FieldTypeKey;
  labelKey: string;
  icon: string;
  keywords: string[];
  descriptionKey: string;
}

export const FIELD_DETECTION_KEYWORDS: IKeywordCategory[] = [
  {
    fieldType: 'signature',
    labelKey: 'fieldDetectionSignature',
    icon: 'edit',
    descriptionKey: 'fieldDetectionSignatureDesc',
    keywords: [
      'signature',
      'sign',
      'sign here',
      'sign your document',
      'insert esignature here',
      'insert e signature here',
      'signer signature',
      'authorized signature',
      'applicant signature',
      'customer signature',
      'client signature',
      'digital signature',
      'esignature',
      'electronic signature',
      'please sign',
      'sign below',
      'sign above',
      'sign and return',
      'signature of applicant',
      'signature with seal',
      'for signature only',
      'executed by',
      'duly signed',
      'signed by',
      'agreed and accepted by',
      'signature block',
      'signature line',
      'signature of',
      'witness signature',
      'notary signature',
      'sign and date',
      'signature date',
      'sign here today',
      'place signature',
      'your signature',
      'signature of signer',
    ],
  },
  {
    fieldType: 'name',
    labelKey: 'fieldDetectionName',
    icon: 'person',
    descriptionKey: 'fieldDetectionNameDesc',
    keywords: [
      'full name',
      'name',
      'your name',
      'legal name',
      'applicant name',
      'customer name',
      'employee name',
      'printed name',
      'name in block letters',
      'authorized person name',
      'signatory name',
      'witness name',
      'name of signer',
      'typed name',
      'name printed',
    ],
  },
  {
    fieldType: 'date',
    labelKey: 'fieldDetectionDate',
    icon: 'calendar_today',
    descriptionKey: 'fieldDetectionDateDesc',
    keywords: [
      'date',
      'date of signing',
      'signing date',
      'execution date',
      'agreement date',
      'dated',
      'date signed',
      'date of execution',
      'effective date',
      'today\'s date',
      'date here',
      'date of birth',
      'dob',
      'completion date',
      'submission date',
    ],
  },
  {
    fieldType: 'email',
    labelKey: 'fieldDetectionEmail',
    icon: 'email',
    descriptionKey: 'fieldDetectionEmailDesc',
    keywords: [
      'email',
      'email address',
      'registered email',
      'official email',
      'contact email',
      'e-mail',
      'email id',
      'work email',
      'personal email',
    ],
  },
  {
    fieldType: 'phone',
    labelKey: 'fieldDetectionPhone',
    icon: 'phone',
    descriptionKey: 'fieldDetectionPhoneDesc',
    keywords: [
      'phone',
      'mobile',
      'contact number',
      'phone number',
      'mobile number',
      'telephone',
      'tel no',
      'cell',
      'mobile no',
      'contact no',
    ],
  },
  {
    fieldType: 'company',
    labelKey: 'fieldDetectionCompany',
    icon: 'business',
    descriptionKey: 'fieldDetectionCompanyDesc',
    keywords: [
      'company name',
      'organization name',
      'firm name',
      'business name',
      'employer name',
      'registered company',
      'company',
      'organization',
      'employer',
    ],
  },
  {
    fieldType: 'address',
    labelKey: 'fieldDetectionAddress',
    icon: 'location_on',
    descriptionKey: 'fieldDetectionAddressDesc',
    keywords: [
      'address',
      'residential address',
      'office address',
      'permanent address',
      'correspondence address',
      'mailing address',
      'street address',
      'full address',
    ],
  },
  {
    fieldType: 'title',
    labelKey: 'fieldDetectionTitleLabel',
    icon: 'badge',
    descriptionKey: 'fieldDetectionTitleDesc',
    keywords: [
      'designation',
      'title',
      'job title',
      'position',
      'role',
      'authorized signatory',
    ],
  },
  {
    fieldType: 'id',
    labelKey: 'fieldDetectionId',
    icon: 'tag',
    descriptionKey: 'fieldDetectionIdDesc',
    keywords: [
      'id number',
      'reference number',
      'customer id',
      'employee id',
      'passport number',
      'aadhaar',
      'pan number',
      'tax id',
      'reference no',
      'ref no',
      'id no',
    ],
  },
  {
    fieldType: 'checkbox',
    labelKey: 'fieldDetectionCheckbox',
    icon: 'check_box',
    descriptionKey: 'fieldDetectionCheckboxDesc',
    keywords: [
      'i agree',
      'i accept',
      'terms and conditions',
      'accept terms',
      'consent',
      'agree to',
      'declaration',
      'confirm',
      'acknowledge',
      'certify',
      'checkbox',
      'check here',
      'tick',
      'yes no',
    ],
  },
];
